import { DateTime } from 'effect';
import { dual } from 'effect/Function';

export const centralTime = DateTime.zoneMakeNamedUnsafe('America/Chicago');

const pad = (width: number) => (value: number) => String(value).padStart(width, '0');

const twelveHour = (hour: number) => hour % 12 || 12;

const named = (options: Intl.DateTimeFormatOptions) => (self: DateTime.DateTime) =>
  DateTime.format(self, { locale: 'en-US', ...options });

const zoneId = (self: DateTime.DateTime) =>
  DateTime.isZoned(self) ? DateTime.zoneToString(self.zone) : 'UTC';

const zoneAbbreviation = (self: DateTime.DateTime) =>
  new Intl.DateTimeFormat('en-US', { timeZone: zoneId(self), timeZoneName: 'short' })
    .formatToParts(DateTime.toEpochMillis(self))
    .find((part) => part.type === 'timeZoneName')?.value ?? zoneId(self);

const offsetIso = (self: DateTime.DateTime) =>
  DateTime.isZoned(self) ? DateTime.zonedOffsetIso(self) : '+00:00';

const fromParts =
  (read: (parts: DateTime.DateTime.PartsWithWeekday) => string) => (self: DateTime.DateTime) =>
    read(DateTime.toParts(self));

const tokens: Record<string, (self: DateTime.DateTime) => string> = {
  YYYY: fromParts(({ year }) => pad(4)(year)),
  YY: fromParts(({ year }) => pad(2)(year % 100)),
  MMMM: named({ month: 'long' }),
  MMM: named({ month: 'short' }),
  MM: fromParts(({ month }) => pad(2)(month)),
  M: fromParts(({ month }) => String(month)),
  DD: fromParts(({ day }) => pad(2)(day)),
  D: fromParts(({ day }) => String(day)),
  dddd: named({ weekday: 'long' }),
  ddd: named({ weekday: 'short' }),
  HH: fromParts(({ hour }) => pad(2)(hour)),
  H: fromParts(({ hour }) => String(hour)),
  hh: fromParts(({ hour }) => pad(2)(twelveHour(hour))),
  h: fromParts(({ hour }) => String(twelveHour(hour))),
  mm: fromParts(({ minute }) => pad(2)(minute)),
  ss: fromParts(({ second }) => pad(2)(second)),
  SSS: fromParts(({ millisecond }) => pad(3)(millisecond)),
  A: fromParts(({ hour }) => (hour < 12 ? 'AM' : 'PM')),
  z: zoneAbbreviation,
  Z: offsetIso,
  ZZ: (self) => offsetIso(self).replace(':', ''),
  LLL: (self) => formatLayout(self, 'MMMM D, YYYY h:mm A'),
};

const tokenPattern =
  /\[([^\]]*)]|LLL|YYYY|YY|MMMM|MMM|MM|M|DD|D|dddd|ddd|HH|H|hh|h|mm|ss|SSS|A|ZZ|Z|z|y+|E+|a+|dd?|'/g;

const dateFnsToken = (layout: string) => (token: string) => {
  throw new Error(
    `formatLayout takes Moment-style tokens, but "${token}" in "${layout}" is date-fns: use YYYY for yyyy, D or DD for d or dd, A for a, dddd or ddd for EEEE or EEE, and [brackets] instead of quotes for literal text.`,
  );
};

export const formatLayout: {
  (layout: string): (self: DateTime.DateTime) => string;
  (self: DateTime.DateTime, layout: string): string;
} = dual(2, (self: DateTime.DateTime, layout: string) =>
  layout.replace(
    tokenPattern,
    (token: string, literal: string | undefined) =>
      literal ?? tokens[token]?.(self) ?? dateFnsToken(layout)(token),
  ),
);
